import GlobalDefaultContext from '@gef-ui/context-defaults';
import doWhen from "@gef-ui/utils/doWhen";
import PropTypes from 'prop-types';
import cx from "ramda-extension/es/cx";
import React, { Component, createElement, isValidElement } from 'react';
import Icon from '../../../atoms/Icon';
import AngleDown from '../../../atoms/SvgIcon/Icons/AngleDown';
import AngleUp from '../../../atoms/SvgIcon/Icons/AngleUp';
import { withDefaultComponentProps } from '../../../decorators/withDefaultComponentProps';
import { DropdownContent } from '../../../molecules/Dropdown';
const COMPONENT_NAME = 'MenuItem';
const IGNORE_CLASS = 'ignore-content-click-level-';
class MenuItem extends Component {
    constructor(props){
        super(props);
        this.state = {
            open: false
        };
        this.menuItemRef = undefined;
    }
    static contextType = GlobalDefaultContext;
    hasIgnoreClass = (node)=>{
        if (node.classList.contains(this.createIgnoreClass())) {
            return true;
        }
        return node.parentNode?.classList ? this.hasIgnoreClass(node.parentNode) : false;
    };
    createIgnoreClass = ()=>IGNORE_CLASS + this.props.subMenuLevel;
    createNestedMenu = (children)=>/*#__PURE__*/ React.createElement(DropdownContent, {
            className: cx(this.createIgnoreClass()),
            visible: true,
            onClickOutside: (e)=>{
                if (!this.menuItemRef.contains(e.target) && this.props.persistOpenState !== true) {
                    this.handleOpen(false);
                }
            },
            snapRight: true,
            expandableFromVertical: this.props.expandableFromVertical
        }, /*#__PURE__*/ React.cloneElement(children, {
            subMenuLevel: this.props.subMenuLevel + 1
        }));
    handleClick = (event)=>{
        doWhen(this.props.children && !this.hasIgnoreClass(event.target), ()=>this.handleOpen(!this.state.open));
    };
    handleOpen = (open)=>{
        this.setState({
            open
        });
    };
    getIconElement = (icon)=>{
        if (/*#__PURE__*/ isValidElement(icon)) {
            return /*#__PURE__*/ React.createElement("span", {
                className: "Menu-icon"
            }, icon);
        } else if (icon) {
            return /*#__PURE__*/ React.createElement(Icon, {
                name: icon,
                className: "Menu-icon"
            });
        }
        return null;
    };
    getToggleIcon = ()=>this.props.children && /*#__PURE__*/ React.createElement("span", {
            className: "Menu-toggle"
        }, this.state.open ? this.props.openIcon : this.props.closeIcon);
    render() {
        const { children, activeClassName, className, label, icon, isActive, horizontalToggle, closeIcon, openIcon, subMenuLevel, expandableFromVertical, openOnHover, link, typeSelect, useList, ...other } = this.props;
        const { NavLink } = link ? this.context.routing.Router : {};
        const listBoxItemProps = {
            className: cx('Menu-item', {
                'Menu-toggle': horizontalToggle
            }, {
                Dropdown: !!children
            }, {
                'ignore-react-onclickoutside': !!children
            }, {
                'Menu-item--typeSelect': typeSelect
            }, className),
            onMouseEnter: openOnHover ? ()=>this.handleOpen(true) : null,
            onMouseLeave: openOnHover ? ()=>this.handleOpen(false) : null,
            ...other
        };
        const itemProps = {
            onClick: this.handleClick,
            ref: (ref)=>{
                this.menuItemRef = ref;
            }
        };
        const iconElement = this.getIconElement(icon);
        const toggleIcon = this.getToggleIcon();
        const customActive = isActive ? isActive() : false;
        const activeLinkClassName = cx({
            'Menu-link--active': !isActive
        }, {
            [activeClassName]: !isActive && !!activeClassName
        });
        const linkClassName = cx('Menu-link', 'Link', {
            'Menu-link--active': customActive
        }, {
            [activeClassName]: customActive && !!activeClassName
        });
        const linkElement = link ? /*#__PURE__*/ React.createElement(NavLink, {
            to: link,
            className: linkClassName,
            activeClassName: activeLinkClassName,
            ...itemProps
        }, iconElement, /*#__PURE__*/ React.createElement("span", {
            className: "Menu-label"
        }, label), !horizontalToggle && toggleIcon) : /*#__PURE__*/ React.createElement("div", {
            className: linkClassName,
            ...itemProps
        }, iconElement, label, !horizontalToggle && toggleIcon);
        return /*#__PURE__*/ createElement(useList ? 'li' : 'div', {
            ...listBoxItemProps
        }, linkElement, children && this.state.open ? this.createNestedMenu(children) : null);
    }
}
MenuItem.propTypes = {
    /**
	 * Custom `activeClassName` used when link is determined as active.
	 */ activeClassName: PropTypes.string,
    /**
	 * Children will be rendered within the `DropdownMenu`.
	 */ children: PropTypes.node,
    /**
	 * Custom `className` for the root.
	 */ className: PropTypes.string,
    /**
	 * Close icon node
	 */ closeIcon: PropTypes.node,
    /**
	 * If true, `DropdownContent` has verticalExpandable class (Typically use for vertical menus)
	 */ expandableFromVertical: PropTypes.bool,
    /**
	 * If true, horizontal toggle will be used instead of vertical
	 */ horizontalToggle: PropTypes.bool,
    /**
	 * Icon used inside of menuItem. Can be one of the Icon.names or the any element itself.
	 */ icon: PropTypes.node,
    /**
	 * Function to determine if the link should be marked as active.
	 */ isActive: PropTypes.func,
    /**
	 * Label of the menu item, can be string or component
	 */ label: PropTypes.node.isRequired,
    /**
	 * React router link path
	 */ link: PropTypes.string,
    /**
	 * OnClick fn
	 *
	 * @param {Object} event - event targeting the element
	 * @return {undefined} Nothing
	 */ onClick: PropTypes.func,
    /**
	 * Open icon node
	 */ openIcon: PropTypes.node,
    /**
	 * If true, menu will open on mouse enter and close on mouse leave
	 */ openOnHover: PropTypes.bool,
    /**
	 * If true, DropdownContent will ignore onClickOutside handler and will be kept open until closed manually
	 */ persistOpenState: PropTypes.bool,
    /**
	 * Level of submenu, no need to set
	 */ subMenuLevel: PropTypes.number,
    /**
	 * If true, will have class Menu-item--typeSelect
	 */ typeSelect: PropTypes.bool,
    /**
	 * Whether to use <li> element
	 */ useList: PropTypes.bool
};
export { MenuItem };
export default withDefaultComponentProps(COMPONENT_NAME, {
    expandableFromVertical: false,
    openIcon: /*#__PURE__*/ React.createElement(AngleUp, null),
    closeIcon: /*#__PURE__*/ React.createElement(AngleDown, null)
}, MenuItem);
